import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { ChevronsUpDown, X, Check, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EQUIPMENT_FLAT_LIST } from '@/lib/equipmentTypes';
import { useIsMobile } from '@/hooks/use-mobile';

interface EquipmentSelectProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  mode?: 'single' | 'multi';
  placeholder?: string;
  className?: string;
  error?: boolean;
}

export const EquipmentSelect: React.FC<EquipmentSelectProps> = ({
  value,
  onChange,
  mode = 'single',
  placeholder = 'Select equipment',
  className,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();

  // Filter flat list based on search input (shared between desktop & mobile)
  const filtered = useMemo(() => {
    if (!search.trim()) return EQUIPMENT_FLAT_LIST;
    const q = search.toLowerCase();
    return EQUIPMENT_FLAT_LIST.filter(
      (t) =>
        t.code.toLowerCase().startsWith(q) ||
        t.name.toLowerCase().includes(q)
    );
  }, [search]);

  // Get display label for a stored enum value
  const getDisplayLabel = (enumVal: string): string => {
    const found = EQUIPMENT_FLAT_LIST.find((t) => t.enumValue === enumVal);
    return found ? `${found.name} — ${found.code}` : enumVal;
  };

  const getShortLabel = (enumVal: string): string => {
    const found = EQUIPMENT_FLAT_LIST.find((t) => t.enumValue === enumVal);
    return found ? found.name : enumVal;
  };

  const handleSelect = (enumVal: string) => {
    if (mode === 'single') {
      onChange(enumVal);
      setOpen(false);
      setSearch('');
    } else {
      const arr = Array.isArray(value) ? value : [];
      if (arr.includes(enumVal)) {
        onChange(arr.filter((v) => v !== enumVal));
      } else {
        onChange([...arr, enumVal]);
      }
      // Multi: keep open on desktop popover (existing behavior closed it; preserve that on desktop)
      if (!isMobile) {
        setOpen(false);
      }
      setSearch('');
    }
  };

  const selectedValues = mode === 'multi' ? (Array.isArray(value) ? value : []) : [];
  const singleValue = mode === 'single' ? (value as string) : '';

  // Shared trigger button
  const triggerButton = (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className={cn(
        'w-full justify-between font-normal h-9',
        !singleValue && selectedValues.length === 0 && 'text-muted-foreground',
        error && 'border-destructive',
        className
      )}
    >
      {mode === 'single' ? (
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">
          {singleValue ? getDisplayLabel(singleValue) : placeholder}
        </span>
      ) : selectedValues.length === 0 ? (
        <span className="overflow-hidden text-ellipsis whitespace-nowrap">{placeholder}</span>
      ) : (
        <span className="flex items-center gap-1 overflow-hidden flex-wrap">
          {selectedValues.slice(0, 2).map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-0.5 h-5 px-2 text-xs rounded-sm bg-muted"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onChange(selectedValues.filter((sv) => sv !== v));
              }}
            >
              {getShortLabel(v)}
              <X className="h-3 w-3 hover:text-red-500 cursor-pointer" />
            </span>
          ))}
          {selectedValues.length > 2 && (
            <span className="inline-flex items-center h-5 px-1.5 text-xs rounded-sm bg-muted text-muted-foreground">
              +{selectedValues.length - 2}
            </span>
          )}
        </span>
      )}
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  // ---------- MOBILE: bottom sheet ----------
  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full text-left"
        >
          {triggerButton}
        </button>
        <Sheet
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setSearch('');
          }}
        >
          <SheetContent
            side="bottom"
            className="h-[85dvh] p-0 flex flex-col gap-0 rounded-t-2xl"
          >
            {/* Sticky header + search */}
            <div className="flex-none border-b bg-background px-4 pt-5 pb-3">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold">Select Equipment</h2>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or code..."
                  className="pl-9 h-10"
                  autoFocus={false}
                />
              </div>
            </div>

            {/* Scrollable list */}
            <div
              className="flex-1 overflow-y-auto overscroll-contain"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {filtered.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No equipment type found.
                </div>
              ) : (
                <ul className="py-1">
                  {filtered.map((item) => {
                    const isSelected =
                      mode === 'single'
                        ? singleValue === item.enumValue
                        : selectedValues.includes(item.enumValue);
                    return (
                      <li key={item.code}>
                        <button
                          type="button"
                          onClick={() => handleSelect(item.enumValue)}
                          className={cn(
                            'w-full flex items-center justify-between px-4 py-3 text-left active:bg-accent',
                            isSelected && 'bg-accent/60'
                          )}
                        >
                          <span className="flex items-center gap-3 min-w-0">
                            <span
                              className={cn(
                                'flex-none h-5 w-5 rounded border flex items-center justify-center',
                                isSelected
                                  ? 'bg-primary border-primary text-primary-foreground'
                                  : 'border-muted-foreground/40'
                              )}
                            >
                              {isSelected && <Check className="h-3.5 w-3.5" />}
                            </span>
                            <span className="text-sm truncate">{item.name}</span>
                          </span>
                          <span className="font-mono text-xs text-muted-foreground ml-3 shrink-0">
                            {item.code}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Sticky footer (multi only) */}
            {mode === 'multi' && (
              <div
                className="flex-none border-t bg-background px-4 py-3"
                style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
              >
                {selectedValues.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3 max-h-20 overflow-y-auto">
                    {selectedValues.map((v) => (
                      <Badge key={v} variant="secondary" className="text-xs gap-1">
                        {getShortLabel(v)}
                        <X
                          className="h-3 w-3 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            onChange(selectedValues.filter((sv) => sv !== v));
                          }}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => onChange([])}
                    disabled={selectedValues.length === 0}
                  >
                    Clear
                  </Button>
                  <Button
                    type="button"
                    className="flex-1"
                    onClick={() => {
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    Done {selectedValues.length > 0 && `(${selectedValues.length})`}
                  </Button>
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  // ---------- DESKTOP: existing popover (UNCHANGED) ----------
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent className="w-[360px] p-0 z-[10005]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type code or name..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[420px]">
            <CommandEmpty>No equipment type found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((item) => {
                const isSelected =
                  mode === 'single'
                    ? singleValue === item.enumValue
                    : selectedValues.includes(item.enumValue);
                return (
                  <CommandItem
                    key={item.code}
                    value={item.code}
                    onSelect={() => handleSelect(item.enumValue)}
                    className={cn(
                      'flex items-center justify-between px-3 py-1.5 cursor-pointer',
                      isSelected && 'bg-accent'
                    )}
                  >
                    <span className="text-sm">{item.name}</span>
                    <span className="font-mono text-xs text-muted-foreground ml-4">
                      {item.code}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>

        {/* Multi-select: show selected tags */}
        {mode === 'multi' && selectedValues.length > 0 && (
          <div className="border-t p-2 flex flex-wrap gap-1">
            {selectedValues.map((v) => (
              <Badge key={v} variant="secondary" className="text-xs gap-1">
                {getShortLabel(v)}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(selectedValues.filter((sv) => sv !== v));
                  }}
                />
              </Badge>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default EquipmentSelect;
